import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { getLiveMatches, getCompletedMatches, getScheduledMatches, getTeams } from '@/lib/supabase';
import { saveMatchRef } from '@/lib/persistence';
import AppHeader from '@/components/AppHeader';
import StartMatchTossModal from '@/components/modals/StartMatchTossModal';
import type { Team } from '@/types';

interface MatchRow {
  id: string;
  matchNumber: number;
  status: string;
  matchTime: string | null;
  startTime: string | null;
  endTime: string | null;
  teamA: Team | null;
  teamB: Team | null;
  tournamentId: string | null;
  teamAId: string;
  teamBId: string;
  createdAt: string;
}

interface ScorerDashboardProps {
  onSignOut: () => void;
}

export default function ScorerDashboard({ onSignOut }: ScorerDashboardProps) {
  const navigate = useNavigate();
  const { profile, isViewer, isScorer, isAdmin } = useAuth();
  const [liveMatches, setLiveMatches] = useState<MatchRow[]>([]);
  const [scheduledMatches, setScheduledMatches] = useState<MatchRow[]>([]);
  const [completedMatches, setCompletedMatches] = useState<MatchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startTarget, setStartTarget] = useState<MatchRow | null>(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rawLive, rawScheduled, rawCompleted, rawTeams] = await Promise.all([
        getLiveMatches(),
        getScheduledMatches(),
        getCompletedMatches(),
        getTeams(),
      ]);

      const teams: Team[] = rawTeams.map((t: any) => ({
        id: t.id,
        name: t.name,
        shortName: t.short_name,
        color: t.color,
        logoUrl: t.logo_url,
        players: [],
      }));

      const mapMatch = (m: any): MatchRow => ({
        id: m.id,
        matchNumber: m.match_number,
        status: m.status,
        matchTime: m.match_time ?? null,
        startTime: m.start_time ?? null,
        endTime: m.end_time ?? null,
        teamA: teams.find(t => t.id === m.team_a_id) ?? null,
        teamB: teams.find(t => t.id === m.team_b_id) ?? null,
        tournamentId: m.tournament_id ?? null,
        teamAId: m.team_a_id,
        teamBId: m.team_b_id,
        createdAt: m.created_at,
      });

      setLiveMatches(rawLive.map(mapMatch));
      setScheduledMatches(rawScheduled.map(mapMatch));
      setCompletedMatches(rawCompleted.map(mapMatch));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleMatchStarted = (matchData: any, match: MatchRow) => {
    setStartTarget(null);
    saveMatchRef({
      matchId: matchData.id,
      teamAId: matchData.team_a_id,
      teamBId: matchData.team_b_id,
      status: 'first-server',
      tournamentId: match.tournamentId,
    });
    navigate(`/match/${matchData.id}/resume`);
  };

  const formatTime = (time: string | null) => {
    if (!time) return null;
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const display = hour % 12 || 12;
    return `${display}:${m} ${ampm}`;
  };

  const canStartMatch = isAdmin || isScorer;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        onSignOut={onSignOut}
        title="Match Dashboard"
        navIcon="home"
        onNavigate={() => navigate('/scorer')}
      />

      <div className="flex-1 overflow-y-auto px-4 py-5 safe-area-bottom max-w-md mx-auto w-full">
        {/* Welcome */}
        {profile && (
          <div className="mb-5">
            <h2 className="text-base font-bold text-text-primary">Welcome, {profile.displayName}</h2>
            <p className="text-xs text-text-muted mt-0.5">
              {isViewer ? 'View matches and tournaments below.' : 'Select a match to start or resume scoring.'}
            </p>
          </div>
        )}

        {/* Quick Action: Tournaments */}
        <button
          onClick={() => navigate('/tournaments')}
          className="card flex items-center gap-3 hover:border-info/50 hover:bg-info/5 transition-colors active:scale-[0.99] w-full mb-3"
        >
          <div className="w-9 h-9 rounded-full bg-info/15 flex items-center justify-center flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-info">
              <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
            </svg>
          </div>
          <span className="text-sm font-bold text-text-primary">View Tournaments</span>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-auto text-text-muted">
            <path d="M8 4l6 6-6 6" />
          </svg>
        </button>

        {/* Quick Action: Switch to Throwball */}
        <a
          href={`${window.location.origin.replace(/volleyball/i, 'throwball')}/scorer`}
          target="_blank"
          rel="noopener noreferrer"
          className="card flex items-center gap-3 hover:border-warning/50 hover:bg-warning/5 transition-colors active:scale-[0.99] w-full mb-5"
        >
          <div className="w-9 h-9 rounded-full bg-warning/15 flex items-center justify-center flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-warning">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 2a15 15 0 0 1 0 20M2 12h20" />
            </svg>
          </div>
          <span className="text-sm font-bold text-text-primary">Throwball Scorer</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-auto text-text-muted">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>

        {error && (
          <div className="bg-error/15 border border-error/30 rounded-lg p-3 mb-4 flex items-center justify-between">
            <p className="text-error text-sm">{error}</p>
            <button onClick={loadDashboard} className="text-xs text-error underline ml-3">Retry</button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="w-8 h-8 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* SCHEDULED MATCHES */}
            {scheduledMatches.length > 0 && (
              <section className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider">SCHEDULED</h3>
                  <span className="text-xs font-bold text-text-muted bg-surface-raised px-2 py-0.5 rounded-full">{scheduledMatches.length}</span>
                </div>
                <div className="space-y-3">
                  {scheduledMatches.map(m => (
                    <MatchCard
                      key={m.id}
                      match={m}
                      statusLabel="SCHEDULED"
                      actionLabel={canStartMatch ? 'Start' : 'Scheduled'}
                      timeDisplay={formatTime(m.matchTime)}
                      onClick={canStartMatch ? () => setStartTarget(m) : undefined}
                      statusBg="bg-surface-raised"
                      statusText="text-text-muted"
                      canStart={canStartMatch}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* LIVE MATCHES */}
            <section className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider">LIVE</h3>
                <span className="text-xs font-bold text-warning bg-warning/15 px-2 py-0.5 rounded-full">{liveMatches.length}</span>
              </div>
              {liveMatches.length === 0 ? (
                <div className="card text-center py-6 border-dashed">
                  <p className="text-text-muted text-sm">No live matches.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {liveMatches.map(m => (
                    <MatchCard
                      key={m.id}
                      match={m}
                      statusLabel={m.status === 'live' ? 'LIVE' : m.status === 'set-won' ? 'SET WON' : 'RESUMING'}
                      actionLabel={isViewer ? 'View' : 'Resume'}
                      onClick={() => navigate(`/match/${m.id}/resume`)}
                      statusBg="bg-warning/20"
                      statusText="text-warning"
                      isLive
                    />
                  ))}
                </div>
              )}
            </section>

            {/* COMPLETED MATCHES */}
            <section className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-text-muted uppercase tracking-wider">COMPLETED</h3>
                <span className="text-xs font-bold text-success bg-success/15 px-2 py-0.5 rounded-full">{completedMatches.length}</span>
              </div>
              {completedMatches.length === 0 ? (
                <div className="card text-center py-6 border-dashed">
                  <p className="text-text-muted text-sm">No completed matches.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {completedMatches.map(m => (
                    <MatchCard
                      key={m.id}
                      match={m}
                      statusLabel="COMPLETED"
                      actionLabel="View"
                      onClick={() => navigate(`/match/${m.id}/resume`)}
                      statusBg="bg-success/20"
                      statusText="text-success"
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {startTarget && startTarget.teamA && startTarget.teamB && (
        <StartMatchTossModal
          matchId={startTarget.id}
          matchNumber={startTarget.matchNumber}
          teamA={startTarget.teamA}
          teamB={startTarget.teamB}
          onClose={() => setStartTarget(null)}
          onStarted={(matchData) => handleMatchStarted(matchData, startTarget)}
        />
      )}
    </div>
  );
}

function MatchCard({
  match,
  statusLabel,
  actionLabel,
  onClick,
  statusBg,
  statusText,
  isLive,
  canStart,
  timeDisplay,
}: {
  match: MatchRow;
  statusLabel: string;
  actionLabel: string;
  onClick?: () => void;
  statusBg: string;
  statusText: string;
  isLive?: boolean;
  canStart?: boolean;
  timeDisplay?: string | null;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`w-full card text-left transition-colors active:scale-[0.99] group ${onClick ? 'hover:border-text-muted cursor-pointer' : 'cursor-default opacity-70'}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-text-muted">MATCH #{match.matchNumber}</span>
          {match.tournamentId && (
            <span className="text-[10px] text-text-muted bg-surface-raised px-1.5 py-0.5 rounded">TOURNAMENT</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isLive && <span className="w-2 h-2 rounded-full bg-warning animate-pulse" />}
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${statusBg} ${statusText}`}>
            {statusLabel}
          </span>
        </div>
      </div>

      {match.teamA && match.teamB ? (
        <div className="flex items-center justify-between">
          <TeamDisplay team={match.teamA} />
          <span className="text-xs font-bold text-text-muted px-2">VS</span>
          <TeamDisplay team={match.teamB} align="right" />
        </div>
      ) : (
        <p className="text-sm text-text-muted text-center py-1">Teams loading...</p>
      )}

      {timeDisplay && (
        <p className="text-xs text-text-muted mt-2">{timeDisplay}</p>
      )}

      {onClick && (
        <div className={`flex items-center justify-end mt-3 gap-1 transition-colors ${canStart ? 'text-success' : 'text-text-muted group-hover:text-text-primary'}`}>
          <span className="text-xs font-semibold">{actionLabel}</span>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 4l6 6-6 6" />
          </svg>
        </div>
      )}
    </button>
  );
}

function TeamDisplay({ team, align = 'left' }: { team: Team; align?: 'left' | 'right' }) {
  return (
    <div className={`flex items-center gap-2 flex-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
      {team.logoUrl ? (
        <img src={team.logoUrl} alt={team.shortName} className="w-5 h-5 rounded object-contain flex-shrink-0" onError={e => { e.currentTarget.style.display = 'none'; }} />
      ) : (
        <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: team.color }} />
      )}
      <span className="text-sm font-bold truncate" style={{ color: team.color }}>{team.shortName}</span>
    </div>
  );
}
